import { useEffect } from "react";
import { useSettings } from "./SettingsContext";

/**
 * Injects Google Analytics 4 + Meta Pixel scripts based on admin settings.
 * Tracks SPA route changes via custom events (window.gtag / fbq).
 */
export default function AnalyticsInjector() {
  const { settings, loaded } = useSettings();

  useEffect(() => {
    if (!loaded) return;
    const { ga4_id, meta_pixel_id } = settings;

    // Defer analytics injection until browser is idle to not block LCP.
    const injectAll = () => {
      // GA4
      if (ga4_id && ga4_id !== "G-XXXXXXXXXX" && !document.getElementById("ga4-base")) {
        const s1 = document.createElement("script");
        s1.async = true;
        s1.id = "ga4-base";
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${ga4_id}`;
        document.head.appendChild(s1);

        const s2 = document.createElement("script");
        s2.id = "ga4-init";
        s2.text = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${ga4_id}');
        `;
        document.head.appendChild(s2);
      }

      // Meta Pixel
      if (
        meta_pixel_id &&
        meta_pixel_id !== "000000000000000" &&
        !document.getElementById("meta-pixel")
      ) {
        const s = document.createElement("script");
        s.id = "meta-pixel";
        s.text = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${meta_pixel_id}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(s);
      }
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(injectAll, { timeout: 3000 });
    } else {
      setTimeout(injectAll, 1500);
    }
  }, [loaded, settings]);

  // SPA route change tracking
  useEffect(() => {
    const handler = () => {
      if (typeof window.gtag === "function" && settings.ga4_id) {
        window.gtag("event", "page_view", {
          page_path: window.location.pathname,
        });
      }
      if (typeof window.fbq === "function" && settings.meta_pixel_id) {
        window.fbq("track", "PageView");
      }
    };
    window.addEventListener("popstate", handler);
    // pushstate patch
    const origPush = window.history.pushState;
    window.history.pushState = function () {
      origPush.apply(this, arguments);
      setTimeout(handler, 50);
    };
    return () => {
      window.removeEventListener("popstate", handler);
      window.history.pushState = origPush;
    };
  }, [settings]);

  return null;
}

/**
 * Helper to track WhatsApp CTA clicks as conversions.
 */
export function trackWhatsAppLead(label = "wa_click") {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        method: "whatsapp",
        event_label: label,
      });
    }
    if (typeof window.fbq === "function") {
      window.fbq("track", "Contact", { source: label });
    }
  } catch {
    /* no-op */
  }
}
